<template>
    <div>
        <!-- dialog add report -->
        <v-row class="mt-1 mb-2 mx-1">
            <v-col cols="2" class="pa-1">
                <v-autocomplete
                    v-model="classification"
                    label="Classification of notes"
                    :items="classificationReport"
                    item-text="name"
                    item-value="id"
                    clearable
                    outlined
                    dense
                    hide-details
                    :disabled="loading"
                />
            </v-col>

            <v-col cols="auto" class="py-0">
                <v-radio-group
                    v-model.number="dateType"
                    row
                    class="mt-1"
                    hide-details
                    :disabled="loading"
                >
                    <v-radio
                        :label="$t('daily-date')"
                        :value="1"
                    ></v-radio>
                    <v-radio
                        :label="$t('from-date') + ' / ' + $t('to-date')"
                        :value="2"
                    ></v-radio>
                </v-radio-group>
            </v-col>

            <!-- filter -->
            <v-col v-if="dateType === 2" cols="12" sm="6" md="2" class="pa-1">
                <bee-date-picker
                    v-model="fromDate"  
                    :label="$t('inputs.start-date')"
                    menu-picker
                    dense
                    hide-details
                    :input-props="{
                        outlined: true,
                        dense: true,
                        clearable: true
                    }"
                    :disabled="loading"
                    :allowed-dates="disableDates"
                ></bee-date-picker>
            </v-col>

            <v-col v-if="dateType === 2" cols="12" sm="6" md="2" class="pa-1">
                <bee-date-picker
                    v-model="toDate"
                    :input-label="$t('inputs.end-date')"
                    menu-picker
                    dense
                    hide-details
                    :input-props="{
                        outlined: true,
                        dense: true,
                        clearable: true
                    }"
                    :disabled="loading"
                    :allowed-dates="disableDates"
                ></bee-date-picker>
            </v-col>

            <v-col v-if="dateType === 1" cols="12" sm="6" md="3" class="pa-1">
                <bee-date-input
                    v-model="date"
                    :append-icon="$i18n.locale === 'en' ? 'mdi-menu-right' : 'mdi-menu-left' "
                    :prepend-icon="$i18n.locale === 'en' ? 'mdi-menu-left' : 'mdi-menu-right' "
                    :label="$t('inputs.date')"
                    input-format-order="DD-MM-YYYY"
                    dense
                    :loading="loading"
                    hide-details
                    @click:append="
                        needToRestShownItems = true;
                        addDate();
                    "
                    @click:prepend="
                        needToRestShownItems = true;
                        subDate();
                    "
                ></bee-date-input>
            </v-col>

            <!-- string -->
            <v-col cols="2" class="pa-1">
                <v-text-field 
                    v-model="query"
                    label="search"
                    outlined
                    dense
                    hide-details
                    :disabled="loading"
                />
            </v-col>
            <!-- actions -->
            <v-col cols="auto" class="px-0 pa-1" align-self="center">
                <v-btn
                    color="success"
                    class="px-sm-4 mx-2"
                    :loading="loading"
                    :disabled="loading"
                    @click="getNote();"
                    small
                >
                    <v-icon> mdi-magnify </v-icon>
                    Search
                </v-btn>

                <v-btn
                    color="alert-color white--text"
                    class="px-sm-8"
                    :disabled="loading"
                    @click="clearFilter(); getNote()"
                    small
                >
                    Reset
                </v-btn>
            </v-col>
        </v-row>
        <bee-handy-table
            :items="dailyReports"
            :headers="headers"
            zebra
            dense
            hide-default-footer
            :items-per-page="-1"
            :loading="loading"
        >
            <template v-slot:item.classification="{ item }">
                {{ classificationReport.find(c => c.id === item.classification).name }}
            </template>
        </bee-handy-table>
    </div>
</template>

<script>
import { classificationReport } from '@/helpers/enums'
import { mapState } from 'vuex';
import moment from 'moment';
import { isDisabledFriday, enableVacations } from '@/configs/global.config'
import { disableDates } from '@/helpers/functions'
export default {
    data () {
        return {
            dialog: false,
            reportId: null,
            classificationReport,
            loading: false,
            fromDate: moment(moment().startOf('month')).format('YYYY-MM-DD'),
            toDate: moment().format('YYYY-MM-DD'),
            classification: null,
            query: null,
            dateType: 1,
            date: moment().format('YYYY-MM-DD'),
            isDisabledFriday,
            enableVacations,
            disableDates
        }
    },

    computed: {
        ...mapState({
            medicalRepsBrief: state => state.medicalreps.medicalRepsBrief,
            dailyReports: state => state.dailyReport.dailyReports,
            vacationsDayDetails: state => state.vacations.vacationsDayDetails
        }),
        headers () {
            return [
                {
                    text: this.$t('headers.report'),
                    name: 'report',
                    value: 'report'
                },

                {
                    text: this.$t('headers.employee'),
                    name: 'employee',
                    value: 'employeeId',
                    setValueExpr: val => this.medicalRepsBrief.find(c => c.id === val)?.name
                },

                {
                    text: this.$t('headers.class'),
                    name: 'classification',
                    value: 'classification'
                }
            ]
        }
    },

    methods: {
        moment,

        getNote() {
            this.loading = true
            this.$store.dispatch('dailyReport/fetchByFilter', {
                fromDate: this.dateType === 2 ? this.fromDate : this.date,
                toDate: this.dateType === 2 ? this.toDate : this.date,
                classification: this.classification,
                query: this.query
            }).finally(() => {
                this.loading = false
            })
        },

        clearFilter() {
            this.query = ""
            this.fromDate = null
            this.toDate = null
            this.classification = null
        },

        addDate () {
            this.date = moment(this.date).add(1, 'day').format('YYYY-MM-DD');
            if (
                (this.moment(this.date).format('dddd') === 'Friday' && this.isDisabledFriday) || 
                (this.enableVacations && this.vacationsDayDetails.includes(this.date))
            ) {
                this.addDate()
            }
            return this.date
        },

        subDate() {
            this.date = moment(this.date).subtract(1, 'day').format('YYYY-MM-DD')
            if (
                (this.moment(this.date).format('dddd') === 'Friday' && this.isDisabledFriday) || 
                (this.enableVacations && this.vacationsDayDetails.includes(this.date))
            ) {
                this.subDate()
            }
            return this.date
        },
    },

    created() {
        this.loading = true
        if (enableVacations && !this.vacationsDayDetails.length) this.$store.dispatch('vacations/fetchAll')
        if (!this.medicalRepsBrief.length) {
            this.$store.dispatch('medicalreps/fetchAllBrief', true).then(() => {
                this.getNote()
            })
        } else {
            this.getNote()
        }
    }
}
</script>
